import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Box } from '@mui/material'
import { useSelector } from 'src/utils/store'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { actions } from '../../TransfersState'
import MultipleSelect from 'src/components/MultipleSelect/MultipleSelect'
import * as S from './TransfersManagementFilters.styles'
import { GetTransfersListsPayload } from '../../TransfersTypes'
import { defaultFulfillmentCenterTypeFilters } from 'src/features/fulfillmentCenterFilters'
import { selectFulfillmentCenters } from 'src/features/selector'
import { actions as appActions } from 'src/features/state'

const TransfersManagementFilters = () => {
  const {
    filtersSearchLists, tableTransfersListsPagination, lastGetTransfersListsPayload,
  } = useSelector((state) => state.transfers)

  const fulfillmentCenters = useSelector(selectFulfillmentCenters)

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(appActions.getAvailableFulfillmentCenters(defaultFulfillmentCenterTypeFilters))
  }, [dispatch])

  useEffect(() => {
    dispatch(actions.getSearchFilters())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    fcs, status, priorities,
  } = filtersSearchLists

  const {
    register, handleSubmit, formState: { errors }, clearErrors,
  } = useForm<GetTransfersListsPayload>()

  const sourceFcs = useMemo(() => fcs.map((fc) => ({
    id: fc.id,
    description: fc.name,
  })), [fcs])

  const destinationFcs = useMemo(() => fcs.map((fc) => ({
    id: fc.id,
    description: fc.name,
  })), [fcs])

  const prioritiesOptions = useMemo(() => priorities.map((priority, index) => ({
    id: index + 1,
    description: priority,
  })), [priorities])

  const statusOptions = useMemo(() => status.map((s) => ({
    id: s.id,
    description: s.status,
  })), [status])

  const validateFcAccess = (sourceFcId: string, destinationFcId: string) => {
    const userFcIds = fulfillmentCenters.map((fc) => fc.id.toString())
    const isSourceValid = sourceFcId && userFcIds.includes(sourceFcId)
    const isDestinationValid = destinationFcId && userFcIds.includes(destinationFcId)

    return isSourceValid || isDestinationValid
  }

  const onSubmit = (formData: GetTransfersListsPayload) => {
    clearErrors(['idSourceFc', 'idDestinationFc'])

    const sourceFcId = formData.idSourceFc?.toString() || ''
    const destinationFcId = formData.idDestinationFc?.toString() || ''

    if (sourceFcId && destinationFcId && !validateFcAccess(sourceFcId, destinationFcId)) {
      toast.error('Você precisa ter acesso a pelo menos um FC (origem ou destino) selecionado')
      return
    }

    dispatch(actions.setProperty({ property: 'selectedTransfersLists', value: [] }))

    dispatch(actions.getTransfersLists({
      ...formData,
      page: 1,
      perPage: tableTransfersListsPagination.perPage,
      order: tableTransfersListsPagination.order,
      orderBy: tableTransfersListsPagination.orderBy,
    }))
  }

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '.6rem 1rem',
        width: '100%',
        margin: '1.3rem 0 1.3rem 0',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '.6rem 1rem',
        }}
      >
        <S.CustomSelect
          name='idSourceFc'
          inputProps={{
            ...register('idSourceFc'),
          }}
          autoComplete='off'
          id='idSourceFc'
          label='FC de Origem'
          variant='outlined'
          options={sourceFcs}
          error={!!errors.idSourceFc}
          helperText={errors.idSourceFc?.message}
          optionNone
          type='text'
          fullWidth={false}
          autoFocus
          maxHeight='600px'
          dataTestId='source-fc-test-id'
          defaultValue={lastGetTransfersListsPayload.idSourceFc}
        />
        <S.CustomSelect
          name='idDestinationFc'
          inputProps={{
            ...register('idDestinationFc'),
          }}
          autoComplete='off'
          id='idDestinationFc'
          label='FC de Destino'
          variant='outlined'
          options={destinationFcs}
          error={!!errors.idDestinationFc}
          helperText={errors.idDestinationFc?.message}
          optionNone
          type='text'
          fullWidth={false}
          maxHeight='600px'
          dataTestId='destination-fc-test-id'
          defaultValue={lastGetTransfersListsPayload.idDestinationFc}
        />
        <S.CustomTextField
          name='createdDate'
          id='createdDate'
          label='Data de criação'
          type='date'
          InputLabelProps={{ shrink: true }}
          inputProps={{
            ...register('createdDate'),
            'data-testid': 'created-date-test-id',
          }}
          error={!!errors.createdDate}
          helperText={errors.createdDate?.message}
          defaultValue={lastGetTransfersListsPayload.createdDate}
        />
        <S.CustomTextField
          name='shippingDate'
          id='shippingDate'
          label='Data de expedição'
          type='date'
          InputLabelProps={{ shrink: true }}
          inputProps={{
            ...register('shippingDate'),
            'data-testid': 'shipping-date-test-id',
          }}
          error={!!errors.shippingDate}
          helperText={errors.shippingDate?.message}
          defaultValue={lastGetTransfersListsPayload.shippingDate}
        />
        <MultipleSelect
          register={register}
          inputName='priorities'
          label='Prioridades'
          errors={errors.priorities}
          options={prioritiesOptions}
          dataTestId='priorities-test-id'
          maxHeight='600px'
          width='15rem'
          optionProperty='description'
          searchBar
          selectAll
          defaultOptions={lastGetTransfersListsPayload.priorities}
        />
        <MultipleSelect
          register={register}
          inputName='idsStatus'
          label='Status'
          errors={errors.idsStatus}
          options={statusOptions}
          dataTestId='ids-status-test-id'
          maxHeight='600px'
          width='15rem'
          optionProperty='id'
          searchBar
          selectAll
          defaultOptions={lastGetTransfersListsPayload.idsStatus}
        />
      </Box>
      <S.CustomButton
        title='Buscar'
        type='submit'
        bgColor='blue'
        hoverColor='darkBlueHover'
        letterColor='white'
        data-testid='search-lists-button-test-id'
      />
    </Box>
  )
}

export default TransfersManagementFilters
